/**
 * §3.1 / §UC-04 / §UC-05 / §UC-06: 通知トリガー判定の状態マシン (pure)。
 *
 * AND 条件:
 *   1. 口が開いている (`effectiveMar` が threshold 超過)
 *   2. 一定時間以上継続している (既定 3000ms)
 *   3. 非発話中 (`effectiveIsSpeaking=false`)
 *   4. ミュート OFF (呼び出し側が判定)
 *   5. 直近通知からクールダウン以上経過 (既定 5000ms)
 *
 * 入力: 1 フレームごとの `DetectionInput` (= EffectiveFrame + muted + timestamp)
 * 出力: 通知を発火すべきか + 内部状態の更新 (関数型ステートマシン)
 *
 * 発話中・閾値以下・顔未検出 (1 frame でも)・`suppressFire=true` のいずれかでカウンタ (openSince) は
 * リセットされる (§3.1 異常検知ロジック)。 `suppressFire` (= smile / yawn 形状 = 本物のぽかんでない) は
 * **開口判定のリセット要因 (B 層)** として扱う: issue #283 / 笑顔レイヤー診断 (scripts/analyze-smile-layer.ts) で、
 * smile を「fire のみ抑止 (openSince 維持)」 から「openSince リセット」 に変えると corpus 上 smile FP が
 * 構造的に減り (sustained-smile を保持中に smile 判定が外れた瞬間の trailing-edge spurious fire を防ぐ)、
 * TP / TP-oblique / yawn-then-open / interrupted-sustain の retention は不変と実証されたため (= measure
 * できる + 効く施策)、 旧 C 層 suppress (fire のみ抑止) から B 層 reset に変更した。
 *
 * ADR 0002 (issue #108 / #134、 candidate-aware transformation 層): 入力 4 field
 * (`effectiveMar` / `effectiveIsSpeaking` / `effectiveFaceDetected` / `suppressFire`) は dev-only
 * の transformation 層が `EffectiveFrame` として組み立てる。 production 経路 (= `detection-loop`)
 * は raw signal をそのまま (effectiveMar = normalizedMar、 effectiveIsSpeaking = isSpeaking、
 * effectiveFaceDetected = faceDetected、 suppressFire = smile-blendshape-gate 出力) で組み立てて
 * 呼ぶ — transformation 層は production には介入しない (= ADR 0002 §帰結)。
 *
 * issue #265 (dwell-fraction / Schmitt、 ADR 0011 Accepted 2026-06-08): 発火の「時間判定」 を
 * `firingMode` で 3 方式に切替できる。 production default は `DEFAULT_TUNING` で **`"schmitt"`**
 * (enter 0.30 / exit 0.22、 = candidate `schmitt-e030-x022`) に確定 (eval evidence で best balance:
 * TP 0.920→1.000 / TP-oblique 0.636→0.818 / interrupted-sustain 0.760→0.840 / misdet FP 軽微増)。
 * `firingMode` 未指定 (= undefined) は `"sustain"` (= 従来の連続 openSince + 単発 dip 全 reset) に
 * fallback するが、 production は明示 schmitt。 dwell は FP 4-6x 悪化で不採用、 eval substrate
 * (candidate JSON の tuning) からのみ有効化される。 dwell / Schmitt の機序と選択根拠は
 * `docs/adr/0011-dwell-fraction-temporal-detection.md` / `docs/plans/2026-06-05-001-feat-dwell-fraction-detection-plan.md`。
 *   - `"sustain"`: 連続 ≥`sustainMs` 開口。 1 frame でも閾値割れ/発話/顔ロストで `openSince` を全 reset。
 *   - `"schmitt"`: enter=`threshold`(0.30) で開判定に入り、 exit=`exitThreshold`(0.22) を下回るまで
 *     開を維持する 2 閾値ヒステリシス。 単発 dip (exit〜enter 帯) で `openSince` を消さない安価な reset 緩和。
 *   - `"dwell"`: 直近 `dwellWindowMs` 窓の時間加重開口占有率が `dwellFraction` 以上で発火 (PERCLOS 流)。
 *     単発 dip は占有率を微減させるだけで起点を消さない = self-erase の要因2 (クロック reset) を断つ。
 */

type DwellSample = {
  /** frame timestamp (ms)。 */
  t: number;
  /** その frame が「開口」 と判定されたか (= !speaking && effectiveMar >= threshold && faceあり)。 */
  open: boolean;
};

export type DetectionState = {
  openSince: number | null;
  lastFiredAt: number | null;
  /**
   * issue #265 (dwell mode のみ使用)。 直近 `dwellWindowMs` 窓内の per-frame 開口判定リング
   * (時刻昇順、 末尾が最新)。 sustain / schmitt では undefined のまま (= 触らない)。
   */
  dwellSamples?: DwellSample[];
  /**
   * issue #265 (dwell mode のみ使用)。 現在の dwell episode 起点 = 占有率が `dwellFraction` を
   * 上回り始めた timestamp。 占有率が `dwellFraction` を下回ると null に戻る (= episode 終了 → re-arm)。
   * sustain の `openSince` と同型の「区間」 で、 `lastFiredAt >= episodeSince` で同 episode の重複発火を防ぐ。
   */
  episodeSince?: number | null;
};

export type DetectionInput = {
  /** EffectiveFrame.effectiveMar: 0..1 の正規化 MAR (transformation 層 or raw normalizedMar)。 */
  effectiveMar: number;
  /** EffectiveFrame.effectiveIsSpeaking: 発話判定 (transformation 層 or raw isSpeaking)。 */
  effectiveIsSpeaking: boolean;
  /** EffectiveFrame.effectiveFaceDetected: 顔検出 (transformation 層 or raw faceDetected)。 */
  effectiveFaceDetected: boolean;
  /**
   * EffectiveFrame.suppressFire: smile / yawn 形状 (= 本物のぽかんでない) を表す flag。
   * `effectiveIsSpeaking` と完全に対称な **開口判定リセット要因 (B 層、 issue #283)** として消費される:
   * true なら発話 / 閾値割れ / 顔ロストと同格に `openSince` をリセットする (sustain/schmitt)。 dwell では
   * open=false の 1 サンプル + 現フレームガード (stepDwell 内、 発火のみ見送り占有率は維持)。 次 frame で
   * false に戻り口が開いていれば openSince が新たに立ち上がり、 sustain を満たし直してから fire する。
   *
   * 旧実装は C 層 suppress のみ (openSince を維持し fire 出力だけ抑止) で、 sustained-smile 保持中に
   * smile 判定が 1 frame 外れた瞬間に sustain 充足済みで発火する trailing-edge spurious FP を許していた。
   * issue #283 の笑顔レイヤー診断 (scripts/analyze-smile-layer.ts) で B 層 reset の追加が corpus 上
   * smile FP を retention 無退行で削減すると実証されたため変更した (= ADR 0002 §排他制約は不変、
   * detection-machine 内の field 解釈のみ変更)。 その follow-up で `resolveFire` 内に残していた C 層
   * ガードも dwell 経路の現フレームガードへ移設し、 C 層 (通知ゲート) は dedup / mute / cooldown の
   * 通知ポリシー専任になった。 名前は「fire を抑止する」 結果を依然表すため据え置き (rename は frozen
   * 診断 script / e2e / CONTEXT を広く churn するため見送り)。
   */
  suppressFire: boolean;
  muted: boolean;
  timestamp: number;
};

type FiringMode = "sustain" | "schmitt" | "dwell";

export type DetectionTuning = {
  /**
   * 0..1 の正規化 MAR 閾値 (既定 0.3)。 issue #151 P1 (2026-05-25) で旧 0.5 から変更
   * (= cycle-2 で vision-vad-v1 + threshold 0.3 が TP retention 0.160→0.760 / FP rate
   * 0.113→0.127、 docs/eval-optimization-progress.md F9)。 schmitt mode では enter 閾値を兼ねる。
   */
  threshold: number;
  /**
   * 開口継続時間の閾値 (既定 3000ms)。 F29 / issue #188 ground truth。 issue#230 #1b の時間ベース化後
   * joint sweep (docs/eval-optimization-progress.md §F35) で再評価し 3000ms を据え置き確定
   * (= TP retention 25/25 完全)。 sustainMs=3500 は misdet FP を下げるが通知レイテンシ +0.5s の
   * UX 変更のため別 issue の熟慮 tuning に委ねる。 `baselineWindowMs > sustainMs` の self-erase 不変条件は
   * detection-loop の起動時 assertion で機械的に保証 (#236)。 sustain / schmitt mode で使用 (dwell は
   * `dwellWindowMs` × `dwellFraction` が実効的な open-time 要件を決める)。
   */
  sustainMs: number;
  /** クールダウン時間 (既定 5000ms)。 全 firingMode 共通。 */
  cooldownMs: number;
  /**
   * issue #265 (ADR 0011 Accepted 2026-06-08): 発火の時間判定方式。 undefined = `"sustain"`
   * (= 従来挙動への fallback)。 production default は `DEFAULT_TUNING` で `"schmitt"` (enter 0.30 /
   * exit 0.22) に確定 — eval (266 trial) で oblique TP rescue (TP-oblique 0.636→0.818) かつ FP
   * 軽微増 (0.012→0.036) の best balance。 dwell は FP 4-6x 悪化で不採用だが、 dwell / sustain の
   * コードパスは eval substrate の A/B 用に candidate JSON から有効化可能なまま残す。
   */
  firingMode?: FiringMode;
  /**
   * issue #265 (dwell mode のみ): 開口占有率を測る時間窓 (ms)。 default `4000`。 `dwellFraction` と
   * の積 (≈ 実効 open-time 要件) を `sustainMs` 近傍に保つことで、 sustain と通知レイテンシを揃えつつ
   * `dwellWindowMs − dwellWindowMs×dwellFraction` 分の中断耐性を得る (= sustainMs 短縮ではない)。
   */
  dwellWindowMs?: number;
  /**
   * issue #265 (dwell mode のみ): 発火に要する窓内開口占有率 (0..1)。 default `0.75`。
   * episode re-arm floor も同値 (= 占有率がこれを下回ると episode 終了)。
   */
  dwellFraction?: number;
  /**
   * issue #265 (schmitt mode のみ): 開判定を維持する exit 閾値 (0..1)。 enter は `threshold`。
   * `exitThreshold >= threshold` は逆ヒステリシスになるため実装側で `threshold` に clamp = sustain 等価。
   * default undefined = `threshold` (= sustain 等価)。
   */
  exitThreshold?: number;
};

export const DEFAULT_TUNING: DetectionTuning = {
  threshold: 0.3,
  sustainMs: 3_000,
  cooldownMs: 5_000,
  // issue #265 / ADR 0011 Accepted (2026-06-08): production default を sustain → schmitt
  // (= candidate `schmitt-e030-x022`) に flip。 eval (266 trial、 report 2026-06-05T06-45-41)
  // で TP 0.920→1.000 / TP-oblique 0.636→0.818 (oblique TP rescue) / interrupted-sustain
  // 0.760→0.840 / misdet FP 0.012→0.036 (軽微) = best balance。 dwell は同 interrupted-sustain
  // 改善ながら FP 4-6x 悪化で不採用。 enter=threshold(0.30) / exit=exitThreshold(0.22)。
  firingMode: "schmitt",
  exitThreshold: 0.22,
};

/** issue #265: dwell mode の関数 default (candidate JSON が省略したときの fallback)。 内部専用。 */
const DEFAULT_DWELL_WINDOW_MS = 4_000;
const DEFAULT_DWELL_FRACTION = 0.75;

/**
 * issue #265 (PR #265 review、 adversarial): dwell の時間加重占有率で「1 sample の open 値が次 sample
 * までの区間を全部 open とみなす」 left-hold の罠を塞ぐ上限。 frame-drop / tab backgrounding で sample 間隔が
 * 数秒空くと、 単発の open frame が窓を埋めて occ≈1.0 の誤発火を起こす。 各 open 区間の寄与をこの上限で
 * clamp し、 観測していない gap 時間を open に計上しない (= 分母は固定 windowMs なので gap は占有率を下げる
 * 方向 = 保守的)。 通常 frame 間隔 (30fps≈33ms / 16fps≈62ms / corpus replay の密フレーム) は上限を下回るため
 * eval 数値には影響しない。 frame-drop 耐性は production 採用時に効く。
 */
const MAX_DWELL_SAMPLE_GAP_MS = 200;

export const INITIAL_STATE: DetectionState = {
  openSince: null,
  lastFiredAt: null,
};

export type DetectionStep = {
  state: DetectionState;
  fire: boolean;
};

/**
 * trigger 充足後の共通ゲートラダー (dedup → mute → cooldown → fire)。 全 firingMode 共通で、
 * `regionSince` (= sustain/schmitt は openSince、 dwell は episodeSince) を「区間」 として
 * 同区間内の重複発火を防ぐ。 sustain 時代の `lastFiredAt >= openSince` dedup と完全同型。
 *
 * 通知ポリシー (dedup / mute / cooldown) のみを判定する純粋な C 層 (通知ゲート) で、 口の状態
 * (発話 / 笑顔 / 閾値 / 顔ロスト) は一切見ない — それらは B 層 (開口判定) の責務 (issue #283
 * follow-up で suppressFire ガードを dwell 経路の現フレームガードへ移設し、 この分離を例外なしにした)。
 */
function resolveFire(
  state: DetectionState,
  input: DetectionInput,
  tuning: DetectionTuning,
  regionSince: number | null,
): DetectionStep {
  // 同一区間の重複通知を防ぐ: lastFiredAt が区間起点以降にあれば既に通知済み。
  if (state.lastFiredAt !== null && regionSince !== null && state.lastFiredAt >= regionSince) {
    return { state, fire: false };
  }

  // §UC-08: ミュート中は通知のみ抑止 (検知ステートマシンは進める)。 区間起点は維持して
  // 「分析は継続」 という要件に沿う。 lastFiredAt は更新しないため、 ミュート解除直後に同じ開口
  // 区間が継続していると sustain 経過 + 直前 fire 不在で発火しうる (= 意図したミュート区間内通知の
  // 巻き返し回避)。
  if (input.muted) return { state, fire: false };

  if (state.lastFiredAt !== null && input.timestamp - state.lastFiredAt < tuning.cooldownMs) {
    return { state, fire: false };
  }

  return {
    state: { ...state, lastFiredAt: input.timestamp },
    fire: true,
  };
}

/**
 * issue #265: 時間加重開口占有率 (PERCLOS 流)。 step 関数として open 状態の階段関数を
 * `[now − windowMs, now]` 区間で積分し windowMs で割る。 左 hold (各サンプルの open 値が次サンプル
 * まで持続)、 最初のサンプル以前は閉口扱い (= cold-start 保守: 分母は固定 windowMs)。 frame 数では
 * なく時間で占有を測るため fps / decimate に依存しない。
 */
export function timeWeightedOccupancy(
  samples: ReadonlyArray<DwellSample>,
  windowMs: number,
  now: number,
): number {
  if (samples.length === 0 || windowMs <= 0) return 0;
  const windowStart = now - windowMs;
  let openMs = 0;
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i];
    if (sample === undefined) continue;
    const next = samples[i + 1];
    const segStart = Math.max(sample.t, windowStart);
    const segEnd = next !== undefined ? next.t : now;
    if (segEnd <= segStart) continue;
    // left-hold gap cap: 1 sample の open 値で埋められる区間を上限で clamp (frame-drop 誤発火対策、
    // MAX_DWELL_SAMPLE_GAP_MS の why コメント参照)。
    if (sample.open) openMs += Math.min(segEnd - segStart, MAX_DWELL_SAMPLE_GAP_MS);
  }
  return openMs / windowMs;
}

function stepDwell(
  prev: DetectionState,
  input: DetectionInput,
  tuning: DetectionTuning,
  faceResetTriggered: boolean,
): DetectionStep {
  const windowMs = tuning.dwellWindowMs ?? DEFAULT_DWELL_WINDOW_MS;
  const fraction = tuning.dwellFraction ?? DEFAULT_DWELL_FRACTION;

  // 開口判定: 顔ロスト (buffer 超え) / 発話 / 閾値割れ / suppressFire (smile/yawn 形状) のいずれも
  // 「閉」。 sustain の reset 条件と同じ要素を、 reset ではなく占有率の 1 サンプルに落とす (= 単発 dip で
  // 区間を消さない核心)。 issue #283 で suppressFire を「開口判定 (B 層)」 要因に統一したため dwell でも
  // open=false 扱い (= sustain/schmitt の openSince リセットと同型)。
  const open =
    !faceResetTriggered &&
    !input.effectiveIsSpeaking &&
    !input.suppressFire &&
    input.effectiveMar >= tuning.threshold;

  // 時刻ベース evict (dynamic-open-baseline / vision-vad と同型) して current を push。
  const windowStart = input.timestamp - windowMs;
  const prevSamples = prev.dwellSamples ?? [];
  const samples: DwellSample[] = [];
  for (const s of prevSamples) {
    if (s.t >= windowStart) samples.push(s);
  }
  samples.push({ t: input.timestamp, open });

  const occ = timeWeightedOccupancy(samples, windowMs, input.timestamp);

  // episode 追跡: 占有率が fraction 以上の連続期間が 1 episode。 下回ると null に戻して re-arm
  // (= sustain の openSince が閉口で消えるのと同型)。 re-arm floor は fraction 同値 (YAGNI、
  // ヒステリシスが要るなら別 knob を ADR 0011 §人間が決めるべき点 に温存)。
  const episodeSince = occ >= fraction ? (prev.episodeSince ?? input.timestamp) : null;

  const state: DetectionState = {
    ...prev,
    openSince: null,
    dwellSamples: samples,
    episodeSince,
  };

  if (occ < fraction) return { state, fire: false };

  // 現フレームガード: dwell は占有率ベースで過去の open 蓄積が窓に残ると、 現フレームが発話中 /
  // 笑顔でも占有率が fraction を満たしうる。 「発話 / 笑顔の最中に通知が出る」 のを防ぐため、 現
  // フレームの口形が本物のぽかんでない間は発火を見送る (占有率・episode は維持 = per-frame ガード
  // であって reset ではない)。 sustain / schmitt は B 層 reset で speaking / suppressFire frame が
  // 発火判定に到達しないため、 このガードは dwell 経路にのみ存在する (issue #283 follow-up で
  // resolveFire 内の suppressFire ガードをここへ移設し、 発話と笑顔を対称化 + C 層を純粋化)。
  if (input.effectiveIsSpeaking || input.suppressFire) return { state, fire: false };

  return resolveFire(state, input, tuning, episodeSince);
}

export function step(prev: DetectionState, input: DetectionInput, tuning: DetectionTuning): DetectionStep {
  const mode: FiringMode = tuning.firingMode ?? "sustain";

  // 顔未検出 = hard reset: 1 frame でも `effectiveFaceDetected=false` なら openSince を reset する。
  // (issue #302 で ADR 0006 §軸 1 の `faceDetectedBufferMs` hysteresis buffer を削除。 census +
  // ablation で全 corpus 上 flip 0 件 = 真の no-op と確定したため。 経緯は ADR 0006 §軸 1 Status)
  const faceResetTriggered = !input.effectiveFaceDetected;

  // dwell mode は openSince タイマーではなく窓占有率で発火する独立経路 (face reset も占有率の 1
  // サンプルとして扱う = 単発 dip で区間を消さない)。
  if (mode === "dwell") {
    return stepDwell(prev, input, tuning, faceResetTriggered);
  }

  if (faceResetTriggered) {
    return { state: { ...prev, openSince: null }, fire: false };
  }

  const state: DetectionState = { ...prev };

  const reset = (): DetectionStep => ({ state: { ...state, openSince: null }, fire: false });

  if (input.effectiveIsSpeaking) return reset();

  // issue #283: suppressFire (smile / yawn 形状 = 本物のぽかんでない) は openSince リセット要因 (B 層)。
  // 発話・閾値割れ・顔ロストと同格で開口継続を打ち切る。 schmitt の exit 帯維持 (closeThreshold) より前に
  // 評価することで、 smile 中は exit ヒステリシスに守られず確実にリセットされる (= sustained-smile の
  // trailing-edge spurious fire を断つ核心)。
  if (input.suppressFire) return reset();

  // issue #265 schmitt mode: 開を維持する close 閾値を enter 閾値より低くして 2 閾値ヒステリシスにする。
  // 開いていない (openSince===null) なら enter=`threshold`、 開いている (openSince!==null) なら
  // exit=`exitThreshold` を要求する。 exitThreshold >= threshold は逆ヒステリシスになるため threshold に
  // clamp (= sustain 等価)。 non-finite (NaN/Infinity) は requiredMar を壊し openSince が永久 latch する
  // ため threshold に倒す (PR #265 review、 adversarial: candidate JSON 誤設定 footgun)。 sustain mode では
  // closeThreshold === threshold で従来挙動と完全一致。
  const rawExit = tuning.exitThreshold;
  const closeThreshold =
    mode === "schmitt" && rawExit !== undefined && Number.isFinite(rawExit)
      ? Math.min(rawExit, tuning.threshold)
      : tuning.threshold;
  const requiredMar = state.openSince !== null ? closeThreshold : tuning.threshold;
  if (input.effectiveMar < requiredMar) return reset();

  const openSince = state.openSince ?? input.timestamp;
  const sustainedFor = input.timestamp - openSince;

  if (sustainedFor < tuning.sustainMs) {
    return { state: { ...state, openSince }, fire: false };
  }

  return resolveFire({ ...state, openSince }, input, tuning, openSince);
}
